import { useEffect, useState } from "react";
import ProductVideo from "./ProductVideo";
import BackgroundVideo from "./BackgroundVideo";
import { products } from "@/mocks/products";
import { track } from "@/lib/analytics";

const heroRotate = products.slice(0, 5);

const pipeline = [
  { icon: "ri-image-line", label: "Foto", hint: "o seu prato" },
  { icon: "ri-sparkling-line", label: "IA", hint: "dá o play" },
  { icon: "ri-video-line", label: "Vídeo", hint: "em movimento" },
  { icon: "ri-smartphone-line", label: "Cardápio", hint: "Play Menu" },
];

export default function Hero() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setInterval(
      () => setActive((a) => (a + 1) % heroRotate.length),
      2600
    );
    return () => clearInterval(timer);
  }, []);

  return (
    <section
      id="top"
      className="grain relative flex min-h-screen items-center overflow-hidden bg-foreground-950 pt-24 pb-20 md:pt-28"
    >
      {/* Comida em movimento — protagonista absoluto */}
      <BackgroundVideo
        src="https://storage.helloreaddy.io/project_files/52886a64-7fa9-42b2-a5b3-49fd386bb6d3/b487ba97-204f-4587-a1a5-d3a484b22e25_WhatsApp-Video-2026-09-16-at-15.50.09.mp4"
        poster="https://readdy.ai/api/search-image?query=Dark%20cinematic%20wide%20landscape%20shot%20of%20gourmet%20dishes%20a%20smash%20burger%20pizza%20and%20cocktail%20arranged%20on%20a%20black%20slate%20table%20with%20warm%20orange%20rim%20lighting%20deep%20shadows%20premium%20food%20photography%20moody%20atmosphere%20subtle%20steam%20high%20detail&width=1600&height=900&seq=playmenu-hero-food-01&orientation=landscape"
        overlayClassName="bg-gradient-to-b from-foreground-950/85 via-foreground-950/55 to-foreground-950"
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_78%_42%,oklch(var(--primary-500)/0.22),transparent_55%)]" />
      </BackgroundVideo>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-12 px-4 md:px-6 lg:grid-cols-[1.02fr_0.98fr] lg:gap-8">
        {/* Conteúdo à esquerda */}
        <div className="flex flex-col items-start">
          <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-foreground-700/60 bg-foreground-800/40 px-4 py-1.5 text-xs font-semibold text-foreground-100 backdrop-blur">
            <span className="h-2 w-2 rounded-full bg-primary-500 animate-pulse-soft" />
            O cardápio que desperta desejo
          </span>

          <h1 className="font-heading text-4xl font-bold leading-[1.04] tracking-tight text-foreground-50 sm:text-5xl md:text-6xl xl:text-[4.4rem]">
            Seu cliente compra primeiro{" "}
            <span className="text-primary-500">com os olhos.</span>
          </h1>

          <p className="mt-6 max-w-xl text-base leading-relaxed text-foreground-300 md:text-lg">
            Transforme seus pratos em experiências visuais que despertam desejo
            antes mesmo do pedido.
          </p>

          <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-400 md:text-base">
            Cardápio em vídeo, inteligência artificial, realidade aumentada e
            tecnologia para restaurantes venderem melhor.
          </p>

          <div className="mt-9 flex w-full flex-col gap-3 sm:flex-row sm:items-center">
            <a
              href="https://playmenu.app/cadastro?tipo=restaurante"
              target="_blank"
              rel="noopener"
              onClick={() => track("hero_cta_click", { cta: "primary" })}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-8 py-4 text-base font-semibold text-foreground-950 shadow-[0_0_40px_-8px_oklch(var(--primary-500)/0.6)] transition-all hover:bg-primary-400 hover:scale-[1.03]"
            >
              Quero transformar meu cardápio
              <i className="ri-arrow-right-line ml-2 text-lg" aria-hidden="true" />
            </a>
            <a
              href="#transformacao"
              onClick={() => track("demo_click", { cta: "hero_secondary" })}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-full border border-foreground-600 px-8 py-4 text-base font-semibold text-foreground-50 transition-colors hover:border-foreground-400 hover:bg-foreground-800/40"
            >
              Ver como funciona
            </a>
          </div>

          <p className="mt-4 text-sm text-foreground-400">
            Sem compromisso.{" "}
            <span className="text-foreground-200">
              Veja seus próprios produtos ganharem vida.
            </span>
          </p>
        </div>

        {/* Comida em movimento à direita — o vídeo é o protagonista */}
        <div className="relative mx-auto w-full max-w-[420px]">
          <div className="absolute -inset-8 -z-10 rounded-[3rem] bg-primary-500/20 blur-3xl" />

          <div className="relative overflow-hidden rounded-[1.75rem] border border-foreground-700/50 bg-foreground-900">
            <div className="aspect-[3/4]">
              {heroRotate.map((product, i) => (
                <div
                  key={product.id}
                  className={`absolute inset-0 transition-opacity duration-700 ${
                    i === active ? "opacity-100" : "opacity-0"
                  }`}
                >
                  <ProductVideo
                    src={product.video}
                    poster={product.image}
                    alt={product.name}
                  />
                  <div className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-foreground-950/95 via-foreground-950/30 to-transparent p-5 pt-20">
                    <span className="inline-block rounded-full bg-primary-500 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-foreground-950">
                      {product.category}
                    </span>
                    <h3 className="mt-2 font-heading text-2xl font-bold text-foreground-50">
                      {product.name}
                    </h3>
                    <div className="mt-1 flex items-center justify-between">
                      <p className="text-lg font-bold text-primary-500">
                        {product.price}
                      </p>
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-foreground-50 text-foreground-950">
                        <i className="ri-add-line text-xl" aria-hidden="true" />
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pipeline visual — Foto → IA → Vídeo → Cardápio */}
          <div className="mt-6 grid grid-cols-4 gap-2">
            {pipeline.map((step, i) => (
              <div key={step.label} className="relative flex flex-col items-center">
                {i < pipeline.length - 1 && (
                  <span className="absolute right-[-6px] top-4 z-10 flex h-4 w-4 items-center justify-center text-primary-500">
                    <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
                  </span>
                )}
                <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-foreground-700/60 bg-foreground-800/60 text-primary-500">
                  <i className={`${step.icon} text-lg`} aria-hidden="true" />
                </span>
                <span className="mt-2 whitespace-nowrap text-[11px] font-bold uppercase tracking-wider text-foreground-100">
                  {step.label}
                </span>
                <span className="mt-0.5 whitespace-nowrap text-[10px] leading-none text-foreground-500">
                  {step.hint}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Indicador de scroll */}
      <a
        href="#desejo"
        aria-label="Rolar para a próxima seção"
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-1 text-foreground-500 transition-colors hover:text-foreground-200 md:flex"
      >
        <span className="text-[11px] uppercase tracking-widest">Role</span>
        <i className="ri-arrow-down-line animate-bounce text-lg" aria-hidden="true" />
      </a>
    </section>
  );
}